import React from 'react';
import styled from 'styled-components';
import { Button, PrimaryButton, TransparentButton } from '../../../components/Buttons';
import { Grid, Breakpoints } from '../../../app/StyleGuide';
import OrgRequiredModal from './OrgRequiredModal';
import CloneTrainingModal from './CloneTrainingModal';
import EasyLinkModal from '../../../components/EasyLinkModal';
import windowService from '../../../services/windowService';
import { User, useUser } from '../../authentication';
import useOrg from '../../../hooks/useOrg';
import useModal from '../../../hooks/useModal';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { Training } from '../models/training';
import { isCourse } from '../utils/isCourse';
import { isPathway } from '../utils/isPathway';
import { NextAction } from '../models/nextAction';
import { UserTrainingAccess } from '../models/UserTrainingAccess';
import { useEasyLink } from '../../../hooks/useEasyLink';
import { getRedirectToLoginFromLoggedOutState } from '../api/getRedirectToLoginFromLoggedOutState';
import capitalizeFilter from '../../../filters/capitalize-filter';
import { purchaseLockedItem } from '../../../utils/purchaseUtils';
import alertService from '../../../services/AlertService';
import { Org } from '../../../contexts/OrgContext';
import getTrainingAction from '../utils/getTrainingAction';

const Buttons = styled.div`
  margin-top: ${Grid._5};
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};

  @media screen and (min-width: ${Breakpoints.screen_md}) {
    margin-top: 0;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const PrimaryActionButton = styled(PrimaryButton)`
  width: 100%;

  @media screen and (min-width: ${Breakpoints.screen_lg}) {
    width: min-content;
    flex: 0;
  }
`;

const ActionButton = styled(Button)`
  flex: 1;
`;

const TransparentActionButton = styled(TransparentButton)`
  flex: 0;
`;

const TrainingPrimaryActionButton = ({
  training,
  user,
  userAccess,
  nextAction,
  openModal,
}: {
  training: Training;
  user: User;
  userAccess: UserTrainingAccess;
  nextAction?: NextAction;
  openModal: (type: string) => void;
}) => {
  const {
    hasGridSubscription,
    hasIndividualGridSubscription,
    hasIndividualLicenseForItem,
    canAccessWithoutSubscription,
    hasPaidForCourse,
  } = userAccess;
  const userCanTakeTraining =
    training.searchAccess === 'Free' ||
    (training.searchAccess === 'Paid' &&
      (hasGridSubscription || hasIndividualGridSubscription || hasIndividualLicenseForItem)) ||
    canAccessWithoutSubscription;

  const instanceId = nextAction?.instanceId;
  const stepId = nextAction?.stepId;

  const assigned = !!instanceId;
  const route = assigned
    ? `#/training-steps/${instanceId}?stepId=${stepId}${training.type === 'pathway' ? '&isWorkflow=true' : ''}`
    : `#/add-to-your-training/${training.id}?noLogout=1&reassign=1`;

  const handleRedirectToLifeWayLoginFromLoggedOutState = () => {
    getRedirectToLoginFromLoggedOutState(
      window.location.href,
      '?route=' + encodeURIComponent(window.location.pathname.substring(1))
    ).then(redirect => {
      if (window.getIsSSO()) {
        windowService.openUrlInSameTab(redirect);
      }
    });
  };

  if (!user) {
    return (
      <PrimaryActionButton onClick={handleRedirectToLifeWayLoginFromLoggedOutState}>
        Log In To Start
      </PrimaryActionButton>
    );
  } else if (!hasPaidForCourse) {
    return (
      <PrimaryActionButton onClick={() => purchaseLockedItem(training.itemNumber) || alertService.showError()}>
        Purchase to Unlock
      </PrimaryActionButton>
    );
  } else if (userCanTakeTraining) {
    const { trainingActionText, handleRedirect } = getTrainingAction(training.type, training.id, nextAction);
    return <PrimaryActionButton onClick={handleRedirect}>{trainingActionText}</PrimaryActionButton>;
  } else {
    return (
      <PrimaryActionButton onClick={() => openModal('unlockTraining')}>
        Start {capitalizeFilter(training.type)}
      </PrimaryActionButton>
    );
  }
};

const AssignTrainingButtons = ({
  training,
  userAccess,
  org,
  openModal,
}: {
  training: Training;
  userAccess: UserTrainingAccess;
  org?: Org;
  openModal: (type: string) => void;
}) => {
  const { canAssign, canAccess, isAdmin } = userAccess;

  if (canAccess) {
    return (
      <>
        {canAssign && <ActionButton onClick={() => openModal('easyLink')}>Assign by Easy Link</ActionButton>}
        {canAssign && (
          <ActionButton onClick={() => windowService.redirectTo(`#/assign-training/${training.id}`)}>
            Assign by Email
          </ActionButton>
        )}
      </>
    );
  } else if (training.searchAccess === 'Free' && !org) {
    return (
      <>
        <ActionButton onClick={() => openModal('orgRequired')}>Assign by Easy Link</ActionButton>
        <ActionButton onClick={() => openModal('orgRequired')}>Assign by Email</ActionButton>
      </>
    );
  } else if (!org || isAdmin) {
    return (
      <>
        <ActionButton onClick={() => openModal('unlockTraining')}>Assign by Easy Link</ActionButton>
        <ActionButton onClick={() => openModal('unlockTraining')}>Assign by Email</ActionButton>
      </>
    );
  }

  return null;
};

const CustomizeButton = ({
  training,
  userAccess,
  user,
  org,
  openModal,
}: {
  training: Training;
  userAccess: UserTrainingAccess;
  user: User;
  org?: Org;
  openModal: (type: string) => void;
}) => {
  const { isAdmin, canAccess, hasGridSubscription } = userAccess;

  const isCPCATraining =
    (isCourse(training) && training.reports.length) ||
    (isPathway(training) && training.entities.some(c => c.taskList?.reports.length));

  const hasBuildPermission = user?.permissions && user?.permissions.filter(p => p === 'Build View').length > 0;

  if (isCPCATraining) {
    return null;
  }

  if (canAccess && hasGridSubscription) {
    if (hasBuildPermission) {
      return (
        <TransparentActionButton onClick={() => openModal('cloneTraining')}>
          <i className="fas fa-edit"></i>Customize
        </TransparentActionButton>
      );
    }
  } else if (canAccess && !hasGridSubscription) {
    if (isAdmin) {
      return (
        <TransparentActionButton onClick={() => openModal('unlockTraining')}>
          <i className="fas fa-edit"></i>Customize
        </TransparentActionButton>
      );
    }
  } else {
    if (!org || isAdmin) {
      return (
        <TransparentActionButton onClick={() => openModal('unlockTraining')}>
          <i className="fas fa-edit"></i>Customize
        </TransparentActionButton>
      );
    }
  }

  return null;
};

export default function TrainingHeaderActions({
  training,
  userAccess,
  nextAction,
}: {
  training: Training;
  userAccess: UserTrainingAccess;
  nextAction?: NextAction;
}) {
  const user = useUser() as User & {
    permissions?: string[];
  };
  const org = useOrg();
  const easyLinkPrefix = useEasyLink();

  const { hasPaidForCourse } = userAccess;

  const [modal, openModal] = useModal((type: string, _: never, dismissModal: () => void) => {
    switch (type) {
      case 'easyLink':
        return (
          <EasyLinkModal
            link={{ orgPrefix: easyLinkPrefix, groupCode: training.id }}
            description={`
                <div>
                  <h4>
                    Anyone who uses this link will automatically be assigned <b>${training.name}</b> and added to your organization.
                  </h4>
                  <h4>
                    Please share this link <b>directly</b> with church members. You can send it many ways including:
                  </h4>
                  <ul style="margin-left: 16px;">
                    <li>Text Messages</li>
                    <li>Facebook and Facebook Messenger</li>
                    <li>Church Management Systems</li>
                    <li>Email</li>
                    <li>A Special Page on Your Website</li>
                  </ul>
                </div>
              `}
            handleDismiss={dismissModal}
          />
        );
      case 'cloneTraining':
        return <CloneTrainingModal training={training} user={user} handleDismiss={dismissModal} />;
      case 'orgRequired':
        return <OrgRequiredModal handleDismiss={dismissModal} />;
      case 'unlockTraining':
        return (
          <ConfirmationModal
            buttonActionText="Get Started"
            buttonType="primary"
            title="Unlock Training"
            prompt={
              "Oh no! It looks like you don't have a subscription. Let's fix that so that you can train and develop others."
            }
            handleSubmit={() => {
              windowService.redirectToPurchase(training.itemNumber);
            }}
            handleDismiss={dismissModal}
          />
        );
      default:
        return null;
    }
  });

  return (
    <Buttons>
      <TrainingPrimaryActionButton
        training={training}
        user={user}
        userAccess={userAccess}
        nextAction={nextAction}
        openModal={openModal}
      />
      {user && hasPaidForCourse && (
        <>
          <AssignTrainingButtons training={training} userAccess={userAccess} org={org} openModal={openModal} />
          <CustomizeButton training={training} userAccess={userAccess} user={user} org={org} openModal={openModal} />
        </>
      )}
      {modal}
    </Buttons>
  );
}
